


import {
  defineComponent,
  computed,
  PropType,
} from '@vue/composition-api';
import type {
  EmergencyResponseTeamReportExt,
} from '@/models/externalReports/emergencyResponseTeam/emergencyResponseTeamReport';
import { dtFormat } from '@/lib/dateHelper';
import { useRoute } from '@/hooks/useRoute';
import { PaneStyle } from '@/models/externalReports/emergencyResponseTeam/emergencyResponseTeamMap';
import {
  PANE_SIDE_HARD_MAX_WIDTH_PX,
  LIST_HARD_MAX_HEIGHT_PX,
} from '@/components/ExternalReports/EmergencyResponseTeam/consts';
import {
  useEmergencyResponseTeamReports,
} from '@/components/ExternalReports/EmergencyResponseTeam/composables/useReports';
import type {
  ExternalReportSettings,
  ExternalReportsLabelPc,
} from '@/models/apis/externalReports/external_report_settings';

export default defineComponent({
  props: {
    userSettings: {
      type: Object as PropType<ExternalReportSettings>,
      required: true,
    },
    paneStyle: {
      type: Object as PropType<PaneStyle>,
      default: () => ({
        paneSideMinWidth: PANE_SIDE_HARD_MAX_WIDTH_PX,
        paneSideMaxWidth: PANE_SIDE_HARD_MAX_WIDTH_PX,
        listMinHeight: LIST_HARD_MAX_HEIGHT_PX,
        listMaxHeight: LIST_HARD_MAX_HEIGHT_PX,
      }),
    },
  },
  emits: ['click-report-on-report-list', 'show-photo-modal'],
  setup(props, { emit }) {
    const { reportState, getSelectedReport } = useEmergencyResponseTeamReports();
    const photoTypes = computed<string[]>(() => props.userSettings.choices.photo_types);
    const pcLabels = computed<ExternalReportsLabelPc>(() => (props.userSettings?.pc ?? {}) as ExternalReportsLabelPc);

    const baseArrivalTsDisp = (report: EmergencyResponseTeamReportExt): string => {
      const baseDepartureDate = dtFormat(report.baseDepartureTs, 'yyyy/mm/dd');
      const baseArrivalDate = dtFormat(report.baseArrivalTs, 'yyyy/mm/dd');
      if (baseDepartureDate === baseArrivalDate) {
        return dtFormat(report.baseArrivalTs, 'HH:MM:SS');
      }
      return dtFormat(report.baseArrivalTs, 'yyyy/mm/dd HH:MM:SS');
    };
    const onClickReportOnReportList = (report: EmergencyResponseTeamReportExt) => {
      emit('click-report-on-report-list', report);
    };
    const { router } = useRoute();
    const onClickReportPrintBtn = (event: MouseEvent, reportId: number) => {
      // 一覧の印刷ボタンを押した時に、報告書の選択イベントが発生しないようにする
      event.stopPropagation();
      const routeObj = {
        name: 'PrintExternalReportsEmergencyResponseTeamReport',
        params: {
          externalReportId: reportId.toString(),
        },
      };
      const obj = router.resolve(routeObj);
      window.open(obj.href, '_blank');
    };
    const getPhotoCountByType = (photoType: string): number => {
      const selectedReport = getSelectedReport();
      if (!selectedReport) return 0;
      return selectedReport.photosExt.filter(photo => photo.photoType === photoType).length;
    };

    const showPhotosByType = (photoType: string) => {
      const selectedReport = getSelectedReport();
      if (!selectedReport) return;

      const photos = selectedReport.photosExt.filter(photo => photo.photoType === photoType);
      emit('show-photo-modal', photoType, photos);
    };
    return {
      // refs
      reportState,
      pcLabels,
      photoTypes,
      // methods
      baseArrivalTsDisp,
      onClickReportOnReportList,
      onClickReportPrintBtn,
      getPhotoCountByType,
      showPhotosByType,
      dtFormat,
    };
  },
});
